import React, { useEffect, useMemo, useState } from "react";
import { Sparkles, Keyboard, Upload } from "lucide-react";
import { formatCurrency } from "../../utils/helpers";
import { formatGlAllocationsLabel } from "../../utils/glCodes";
import { formatMonthLongYm } from "../../utils/invoiceMonthDisplay";

const WAITING_PAGE_SIZE = 10;

function priorInvoiceMonth(ym) {
  if (!ym || !/^\d{4}-\d{2}$/.test(ym)) return "";
  const [y, m] = ym.split("-").map(Number);
  const d = new Date(y, m - 2, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function formatLastInvoiceDay(d) {
  if (!d) return "—";
  const ymd =
    typeof d === "string"
      ? d.slice(0, 10)
      : d instanceof Date && !Number.isNaN(d.getTime())
        ? d.toISOString().slice(0, 10)
        : String(d).slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return "—";
  const date = new Date(`${ymd}T00:00:00`);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

/**
 * Entry chooser for monthly invoice upload: auto-extract vs manual.
 */
const InvoiceUploadMethodChooser = ({
  onChooseExtract,
  onChooseManual,
  onOpenInvoices,
  onUploadPdf,
  waitingRows = [],
  invoiceMonth,
  receivedCount = 0,
  expectedCount = 0,
  closedThroughLabel = "—",
}) => {
  const monthLabel = formatMonthLongYm(invoiceMonth) || "this month";
  const priorTotalLabel = formatMonthLongYm(priorInvoiceMonth(invoiceMonth));
  const priorTotalHeader = priorTotalLabel
    ? `${priorTotalLabel.split(" ")[0].toUpperCase()} TOTAL`
    : "PRIOR TOTAL";
  const [waitingPage, setWaitingPage] = useState(1);
  const waitingTotal = waitingRows.length;
  const waitingTotalPages = Math.max(1, Math.ceil(waitingTotal / WAITING_PAGE_SIZE));
  const waitingStart =
    waitingTotal === 0 ? 0 : (waitingPage - 1) * WAITING_PAGE_SIZE + 1;
  const waitingEnd = Math.min(waitingPage * WAITING_PAGE_SIZE, waitingTotal);
  const pagedWaitingRows = useMemo(
    () =>
      waitingRows.slice(
        (waitingPage - 1) * WAITING_PAGE_SIZE,
        waitingPage * WAITING_PAGE_SIZE,
      ),
    [waitingRows, waitingPage],
  );

  useEffect(() => {
    setWaitingPage(1);
  }, [waitingRows]);

  useEffect(() => {
    if (waitingPage > waitingTotalPages) setWaitingPage(waitingTotalPages);
  }, [waitingPage, waitingTotalPages]);

  return (
    <div>
      <div className="mb-5 flex flex-col xl:flex-row xl:items-end xl:justify-between gap-4">
        <div>
          <p className="text-[12px] font-semibold uppercase tracking-[0.05em] text-[var(--accent,#a51c1c)]">
            Client Administration
          </p>
          <h1 className="mt-1 text-[26px] font-semibold leading-tight text-[var(--ink,#1c1c1a)]">
            Upload Monthly Invoice
          </h1>
        </div>
        <div className="flex flex-wrap items-end gap-2.5">
          <div className="inline-flex items-stretch overflow-hidden rounded-[10px] border border-[var(--line,#e4e2dc)] bg-white shadow-sm">
            <div className="px-3 py-2 border-r border-[var(--line,#e4e2dc)]">
              <label className="block text-[10px] font-semibold uppercase tracking-[0.05em] text-[var(--muted,#6b6a66)]">
                Billing period
              </label>
              <div className="mt-0.5 h-7 inline-flex items-center text-[13px] font-medium text-[var(--ink,#1c1c1a)]">
                {monthLabel}
              </div>
            </div>
            <div className="px-3 py-2 border-r border-[var(--line,#e4e2dc)]">
              <label className="block text-[10px] font-semibold uppercase tracking-[0.05em] text-[var(--muted,#6b6a66)]">
                Received so far
              </label>
              <div className="mt-0.5 h-7 inline-flex items-center text-[13px] font-semibold tabular-nums text-[var(--ink,#1c1c1a)]">
                {receivedCount}
                <span className="ml-1 font-medium text-[var(--muted,#6b6a66)]">
                  of {expectedCount}
                </span>
              </div>
            </div>
            <div className="px-3 py-2">
              <label className="block text-[10px] font-semibold uppercase tracking-[0.05em] text-[var(--muted,#6b6a66)]">
                Closed through
              </label>
              <div className="mt-0.5 h-7 inline-flex items-center text-[13px] font-medium text-[var(--ink,#1c1c1a)]">
                {closedThroughLabel}
              </div>
            </div>
          </div>
        </div>
      </div>

      <p className="text-[14px] text-[var(--muted,#6b6a66)] mb-4">
        Choose how you&apos;d like to add this month&apos;s invoices.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-stretch mb-4">
        <div className="relative flex flex-col rounded-[10px] border-2 border-[var(--accent,#a51c1c)] bg-white p-5 min-h-[180px]">
          <span className="absolute top-4 left-4 inline-flex items-center rounded-full bg-[var(--accent,#a51c1c)] px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.05em] text-white">
            Recommended
          </span>
          <div className="mt-8 flex items-start gap-3 flex-1">
            <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-[var(--accent-soft,#f3e8e8)] text-[var(--accent,#a51c1c)]">
              <Sparkles className="h-4 w-4" aria-hidden />
            </span>
            <div className="min-w-0">
              <h3 className="text-[15px] font-semibold text-[var(--ink,#1c1c1a)]">
                Auto extract from PDF
              </h3>
              <p className="mt-1.5 text-[13px] text-[var(--muted,#6b6a66)]">
                Upload one or more invoice PDFs and AssetLedger fills in the
                fields for you to review before anything is saved.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onChooseExtract}
            className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-md bg-[var(--accent,#a51c1c)] hover:bg-[#8c1a1a] px-4 py-2.5 text-[13px] font-semibold text-white"
          >
            <Upload className="h-4 w-4" aria-hidden />
            Upload PDFs
          </button>
        </div>

        <div className="relative flex flex-col rounded-[10px] border border-[var(--line,#e4e2dc)] bg-white p-5 min-h-[180px]">
          <div className="mt-8 flex items-start gap-3 flex-1">
            <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-[var(--sunken,#eeece7)] text-[var(--muted,#6b6a66)]">
              <Keyboard className="h-4 w-4" aria-hidden />
            </span>
            <div className="min-w-0">
              <h3 className="text-[15px] font-semibold text-[var(--ink,#1c1c1a)]">
                Manual entry
              </h3>
              <p className="mt-1.5 text-[13px] text-[var(--muted,#6b6a66)]">
                Enter invoice details yourself using the existing form. Best for
                a single invoice or a provider without a PDF.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onChooseManual}
            className="mt-6 inline-flex w-full items-center justify-center rounded-md border border-[var(--line-strong,#d5d3cc)] bg-white px-4 py-2.5 text-[13px] font-medium text-[var(--ink,#1c1c1a)] hover:bg-[var(--sunken,#eeece7)]"
          >
            Enter manually
          </button>
        </div>
      </div>

      <div className="rounded-[10px] border border-[var(--line,#e4e2dc)] bg-white overflow-hidden">
        <div className="px-5 py-3.5 flex items-start justify-between gap-3">
          <div>
            <h3 className="text-[14px] font-semibold text-[var(--ink,#1c1c1a)]">
              Still waiting this month
            </h3>
            <p className="mt-0.5 text-[12px] text-[var(--muted,#6b6a66)]">
              Accounts with no {monthLabel.split(" ")[0]} invoice yet. Upload
              straight from the row, or open the account on Invoices.
            </p>
          </div>
          {typeof onOpenInvoices === "function" ? (
            <button
              type="button"
              onClick={onOpenInvoices}
              className="shrink-0 text-[13px] font-medium text-[#3b6ea8] hover:underline"
            >
              Open invoices
            </button>
          ) : null}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-[13px] text-left">
            <thead>
              <tr className="text-[11px] font-semibold uppercase tracking-[0.05em] text-[var(--muted,#6b6a66)] border-t border-[var(--line,#e4e2dc)]">
                <th className="px-5 py-2 font-semibold">Provider</th>
                <th className="px-5 py-2 font-semibold">Account</th>
                <th className="px-5 py-2 font-semibold">GL</th>
                <th className="px-5 py-2 font-semibold">Last invoice</th>
                <th className="px-5 py-2 font-semibold text-right">{priorTotalHeader}</th>
                <th className="px-5 py-2 font-semibold text-right"> </th>
              </tr>
            </thead>
            <tbody>
              {waitingRows.length === 0 ? (
                <tr>
                  <td
                    colSpan={6}
                    className="px-5 py-6 text-center text-[var(--muted,#6b6a66)]"
                  >
                    No accounts are waiting for a {monthLabel} invoice.
                  </td>
                </tr>
              ) : (
                pagedWaitingRows.map((row) => {
                  const gl = formatGlAllocationsLabel(
                    row.gl_allocations,
                    row.gl_code || "",
                  );
                  const isNewAccount = row.row_kind === "new" && row.id == null;
                  const key = `${row.provider}-${row.billing_account}`;
                  return (
                    <tr key={key} className="border-t border-[var(--line,#e4e2dc)]">
                      <td className="px-5 py-2.5 text-[var(--ink,#1c1c1a)]">
                        {row.provider || "—"}
                      </td>
                      <td className="px-5 py-2.5 text-[var(--ink,#1c1c1a)]">
                        {row.billing_account || "—"}
                      </td>
                      <td className="px-5 py-2.5">
                        {gl ? (
                          <span className="inline-flex items-center rounded-full bg-[var(--sunken,#eeece7)] px-2 py-0.5 text-[12px] tabular-nums text-[var(--ink,#1c1c1a)]">
                            {gl}
                          </span>
                        ) : (
                          <span className="text-[var(--accent,#a51c1c)]">No GL</span>
                        )}
                      </td>
                      <td className="px-5 py-2.5 text-[var(--muted,#6b6a66)]">
                        {formatLastInvoiceDay(row.prior_invoice_date)}
                      </td>
                      <td className="px-5 py-2.5 text-right text-[var(--ink,#1c1c1a)] tabular-nums">
                        {isNewAccount
                          ? "new account"
                          : row.prior_total_invoice_amount != null
                            ? formatCurrency(row.prior_total_invoice_amount)
                            : "—"}
                      </td>
                      <td className="px-5 py-2.5 text-right">
                        <button
                          type="button"
                          onClick={() => onUploadPdf?.(row)}
                          className="inline-flex items-center rounded-md border border-[var(--line-strong,#d5d3cc)] bg-white px-3 py-1.5 text-[13px] font-medium text-[var(--ink,#1c1c1a)] hover:bg-[var(--sunken,#eeece7)]"
                        >
                          Upload PDF
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        {waitingTotal > WAITING_PAGE_SIZE ? (
          <div className="px-5 py-3 border-t border-[var(--line,#e4e2dc)] flex flex-col sm:flex-row items-center justify-between gap-2 text-[13px] text-[var(--muted,#6b6a66)]">
            <span>
              Showing {waitingStart}–{waitingEnd} of {waitingTotal}
            </span>
            <div className="flex items-center gap-1.5">
              <button
                type="button"
                disabled={waitingPage <= 1}
                onClick={() => setWaitingPage((p) => Math.max(1, p - 1))}
                className="px-2.5 py-1 border border-[var(--line-strong,#d5d3cc)] rounded-md bg-white hover:bg-[var(--sunken,#eeece7)] disabled:opacity-50"
              >
                Prev
              </button>
              <span className="px-1 text-[var(--ink,#1c1c1a)]">
                Page {waitingPage} of {waitingTotalPages}
              </span>
              <button
                type="button"
                disabled={waitingPage >= waitingTotalPages}
                onClick={() =>
                  setWaitingPage((p) => Math.min(waitingTotalPages, p + 1))
                }
                className="px-2.5 py-1 border border-[var(--line-strong,#d5d3cc)] rounded-md bg-white hover:bg-[var(--sunken,#eeece7)] disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default InvoiceUploadMethodChooser;
